import React from 'react';
import {useSelector} from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {materialityTypes, helixBasisAmount} from '../../../../util/uiconstants';
import {isNullOrUndefined, labels} from '../../../../util/utils';
import EllipsesControl from '../../EllipsesControl/EllipsesControl';
import {
	getAnnualizedAmount,
	getInUserDateFormat,
	getInUserNumberFormat,
	getMeasurementBasisLabel
} from './MeasurementBasisConfig';
import MeasurementBasisListTwo from './MeasurementBasisListTwo';
import TextLink from '../../BaseComponentWrappers/MotifTextLink';
import utilityfunctions from '../../../../util/utilityfunctions';
import MotifTooltip from '../../BaseComponentWrappers/MotifTooltip';
import env from '../../../../util/env';
import {useEngagementId, useUserPreferences, useBlackListAnalyzerTypes} from '../../../../util/customHooks';
import Button from '../../BaseComponentWrappers/MotifButton';

function MeasurementBasisContainer({
	className,
	currentMateriality,
	isCurrentAnalysisDateAmountValuePresent,
	primaryHelixProject,
	isDisabled
}) {
	const userPreferences = useUserPreferences();
	const engagementId = useEngagementId();
	const hasHelixProjects = useSelector((store) => store.configurations?.features?.hasHelixProjects || false);
	const useBlackListAnalyzerTypesValue = useBlackListAnalyzerTypes();

	const handleOnClickNavigate = (e) => {
		e.preventDefault();
		window.open(env.getURL('hercules_web') + '/connections?engagementid=' + engagementId);
	};

	const getHelixLinkTitleTip = () => {
		const setupText = hasHelixProjects ? labels.oarSetupText.split('{0}') : labels.helixNotAvailable;
		return (
			<>
				{hasHelixProjects ? (
					<>
						<span className="TextLabel">{setupText[0]}</span>
						<Button
							variant="text-alt"
							className="helixSetURL"
							onClick={handleOnClickNavigate}
							showLabel
							isEllipsed
							disabled={isDisabled}
							buttonLabel={labels.eyHelixProjects}
							noOfLines={1}
						/>
						<span className="TextLabel">{setupText[1]}</span>
					</>
				) : (
					<>
						<span className="TextLabel">{setupText}</span>
					</>
				)}
			</>
		);
	};

	const elapsedPeriodInMonthsLowerThanDefault = () => {
		return (
			!isNullOrUndefined(currentMateriality) &&
			currentMateriality?.helixModel?.elapsedPeriods < currentMateriality?.helixModel?.totalPeriods
		);
	};

	const showAnnualizedBasisValue = () => {
		return (
			!(
				currentMateriality?.materialityTypeID === materialityTypes.Equity ||
				currentMateriality?.materialityTypeID === materialityTypes.Assets ||
				currentMateriality?.materialityTypeID === materialityTypes.CapitalBasedOther
			) && elapsedPeriodInMonthsLowerThanDefault()
		);
	};

	const measurementBasisLabel = getMeasurementBasisLabel(
		currentMateriality?.materialityTypeID,
		currentMateriality?.otherBasisDescription,
		currentMateriality?.helixModel?.basis
	);

	return (
		<StyledMeasurementBasisContainer
			className={className}
			isCurrentAnalysisDateAmountValuePresent={isCurrentAnalysisDateAmountValuePresent}
		>
			<section className="measurementbasis-list">
				<section className="measurementbasis-type">
					<span className="measurementbasis-header">{labels.measurementBasis}:</span>
					<span className="measurementbasis-value">
						<EllipsesControl content={measurementBasisLabel} tooltip={measurementBasisLabel} noOfLines={1} />
					</span>
				</section>
				{isCurrentAnalysisDateAmountValuePresent && (
					<MeasurementBasisListTwo
						annualizedBasisHeader={`${labels.annualizedBasisValue}:`}
						annualizedBasisValue={getInUserNumberFormat(
							getAnnualizedAmount(
								currentMateriality?.helixModel?.basisAmount || helixBasisAmount.DEFAULT,
								currentMateriality?.helixModel?.elapsedPeriods,
								currentMateriality?.helixModel?.totalPeriods
							),
							userPreferences
						)}
						asofDateHeader={`${labels.asOfDate}:`}
						asofDateValue={getInUserDateFormat(currentMateriality?.helixModel?.analysisDate, userPreferences)}
						basisValueHeader={`${labels.basisValueFromHelix}:`}
						basisValueValue={getInUserNumberFormat(
							currentMateriality?.helixModel?.basisAmount || helixBasisAmount.DEFAULT,
							userPreferences
						)}
						showAnnualizedBasis={showAnnualizedBasisValue()}
						showHelixBasisAmount={currentMateriality?.helixModel?.basisAmount !== helixBasisAmount.DEFAULT}
					/>
				)}
			</section>
			{!useBlackListAnalyzerTypesValue &&
				(hasHelixProjects && primaryHelixProject?.isPrimary ? (
					<TextLink
						variant="alt"
						className="helix-navlink"
						href={utilityfunctions.getMeasurementBasisUrl(primaryHelixProject)}
						title={primaryHelixProject?.isPrimary ? '' : labels.helixNavigationTitle}
						target="_blank"
						label={labels.helixNavigationLink}
						disabled={isDisabled}
					/>
				) : (
					<MotifTooltip className="OARHelixTooltip" variant="alt" trigger={labels.helixNavigationLink} hideCloseButton>
						{getHelixLinkTitleTip()}
					</MotifTooltip>
				))}
		</StyledMeasurementBasisContainer>
	);
}

MeasurementBasisContainer.propTypes = {
	className: PropTypes.string
};

MeasurementBasisContainer.defaultProps = {
	className: 'measurementbasis-container'
};

export default MeasurementBasisContainer;

const StyledMeasurementBasisContainer = styled.section`
	margin-bottom: var(--px-30);
	.measurementbasis-list {
		display: inline-block;
		width: 90%;
		.measurementbasis-type {
			display: inline-block;
			width: ${(props) => (props.isCurrentAnalysisDateAmountValuePresent ? '40%' : '90%')};
			padding-right: var(--px-32);
			.measurementbasis-header {
				display: block;
				font-size: var(--px-12);
				font-weight: 400;
				color: var(--neutrals-700);
				line-height: var(--px-18);
			}
			.measurementbasis-value {
				display: block;
				font-size: var(--px-16);
				color: var(--neutrals-900);
				font-weight: 400;
				line-height: var(--px-24);
			}
		}
	}
	.helix-navlink {
		display: inline-block;
		float: right;
	}
`;
